import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { ButtonIconFields, buttonIconDraft } from '@/components/ButtonIconFields';
import { ButtonStylePicker, type ButtonStyle } from '@/components/ButtonStylePicker';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api, ApiError, getTextsLocale, setTextsLocale,
  type TextCatalog, type TextEntryDTO, type CustomButton,
  type WebappTextCatalog, type WebappTextEntry,
} from '@/lib/api';
import { Accordion } from '@/components/ui/accordion';
import { SegmentControl } from '@/components/ui/segment-control';
import { BottomSheet } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { TelegramEditor } from '@/components/text-editor/TelegramEditor';
import { PremiumEmojiGuide } from '@/components/text-editor/PremiumEmojiGuide';
import { iconColorByKey } from '@/lib/icon-colors';
import { MenuBuilder } from '@/components/MenuBuilder';
import { BulkTextsBar } from '@/components/BulkTexts';
import { BotBubblePreview } from '@/components/BotBubblePreview';
import { PremojiPreview } from '@/components/PremojiPreview';
import { getWebApp, haptic } from '@/lib/telegram';
import { toast } from 'sonner';
import {
  ArrowLeft, Lock, Loader2, RotateCcw, Check, Info, AlertTriangle,
  MessageSquare, MousePointerClick, Sparkles, X, ChevronUp, ChevronDown,
} from 'lucide-react';

const BOT_TABS = ['menu', 'texts', 'buttons', 'custom'] as const;

export const Route = createFileRoute('/bot-texts/$botId')({
  component: BotTexts,
  // Вкладка живёт в адресе, а не в состоянии: иначе экран всегда открывается на
  // одной и той же, а обновление страницы выбрасывает партнёра из конструктора
  // обратно в список текстов. Заодно вкладку можно дать ссылкой.
  validateSearch: (search: Record<string, unknown>): { tab?: BotTab } => {
    const tab = search.tab;
    return BOT_TABS.includes(tab as BotTab) ? { tab: tab as BotTab } : {};
  },
});

// Человекочитаемые названия групп каталога.
/**
 * Где эта кнопка видна клиенту. Нужна не всем: подпись вроде «Назад» говорит сама
 * за себя, а «{plan} · от {price}» партнёр не связывает с ценами. Именно на этом
 * один из них два дня искал, где убрать значок из списка тарифов.
 */
const KEY_HINTS: Record<string, string> = {
  'bot.kb.plan_from': 'Строка тарифа в списке цен',
  'bot.kb.restore_device': 'Возврат удалённого устройства',
};

const GROUP_LABELS: Record<string, string> = {
  start: 'Старт и приветствие',
  menu: 'Меню',
  buy: 'Покупка',
  trial: 'Пробный период',
  promo: 'Промокоды',
  info: 'О сервисе и FAQ',
  support: 'Поддержка',
  locations: 'Локации',
  notify: 'Уведомления',
  profile: 'Профиль',
  refs: 'Рефералы',
  subs: 'Подписки',
  kb: 'Кнопки навигации',
  // Этих групп в словаре не было, и заголовок раздела выходил кодом «mirror», «warmup».
  mirror: 'Свой VPN',
  warmup: 'Автосообщения',
};

type TopTab = 'bot' | 'miniapp';
type BotTab = 'menu' | 'texts' | 'buttons' | 'custom';

function BotTexts() {
  const navigate = useNavigate();
  const { botId } = Route.useParams();
  const { tab } = Route.useSearch();
  const [topTab, setTopTab] = useState<TopTab>('bot');
  // Конструктор первым: он показывает экран целиком, а списки текстов и кнопок —
  // это подробности, за которыми приходят уже потом.
  const botTab: BotTab = tab ?? 'menu';
  const setBotTab = (next: BotTab) =>
    // `replace`, чтобы «назад» уводил со страницы, а не листал вкладки, по которым
    // человек уже прошёлся.
    navigate({ to: '/bot-texts/$botId', params: { botId }, search: { tab: next }, replace: true });

  // Языковая версия текстов двуязычного бота (#274): одна на мини-апп
  // (`setTextsLocale`), её берут каталог и сохранение. Уходя с экрана, возвращаем RU.
  const qc = useQueryClient();
  const botQ = useQuery({ queryKey: ['bot', botId], queryFn: () => api.getBot(botId) });
  const bilingual = botQ.data?.ask_language === true;
  const [locale, setLocale] = useState<'ru' | 'en'>(getTextsLocale);
  const switchLocale = (next: 'ru' | 'en') => {
    if (next === locale) return;
    haptic('light');
    setTextsLocale(next);
    setLocale(next);
    qc.removeQueries({ queryKey: ['bot-texts', botId] });
  };
  useEffect(
    () => () => {
      if (getTextsLocale() !== 'ru') {
        setTextsLocale('ru');
        qc.removeQueries({ queryKey: ['bot-texts', botId] });
      }
    },
    [botId, qc],
  );

  // Мини-апп — заглушка, ничего не грузим пока выбран он.
  const { data, isPending, isError } = useQuery({
    queryKey: ['bot-texts', botId],
    queryFn: () => api.getBotTexts(botId, 1),
    retry: 1,
    enabled: topTab === 'bot',
  });

  // Telegram BackButton → назад на карточку бота.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    let registered: (() => void) | null = null;
    const timer = window.setTimeout(() => {
      tg.BackButton.show();
      const onBack = () => navigate({ to: '/bot' });
      tg.BackButton.onClick(onBack);
      registered = onBack;
    }, 300);
    return () => {
      window.clearTimeout(timer);
      if (registered) tg.BackButton.offClick(registered);
      tg.BackButton.hide();
    };
  }, [navigate, botId]);

  // Записи текущего под-таба (тексты / кнопки), сгруппированные по `group`.
  const grouped = useMemo(() => {
    if (!data) return [];
    const wantKind: TextEntryDTO['kind'] = botTab === 'texts' ? 'text' : 'button';
    const byGroup = new Map<string, TextEntryDTO[]>();
    for (const e of data.entries) {
      if (e.kind !== wantKind) continue;
      const arr = byGroup.get(e.group) ?? [];
      arr.push(e);
      byGroup.set(e.group, arr);
    }
    const ordered = [
      ...data.groups.filter((g) => byGroup.has(g)),
      ...[...byGroup.keys()].filter((g) => !data.groups.includes(g)),
    ];
    return ordered.map((g) => ({ group: g, entries: byGroup.get(g)! }));
  }, [data, botTab]);

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-1 pt-2 flex items-center gap-3">
        <Link
          to="/bot"
          className="w-9 h-9 rounded-[12px] surface-inset flex items-center justify-center shrink-0 active:scale-95 transition-transform"
          aria-label="Назад"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </Link>
        <h2 className="font-display text-lg font-semibold tracking-tight">Тексты и кнопки</h2>
      </div>

      {/* Верхний переключатель: Бот / Мини-апп. */}
      <SegmentControl<TopTab>
        value={topTab}
        onChange={setTopTab}
        options={[
          { value: 'bot', label: 'Бот' },
          { value: 'miniapp', label: 'Мини-апп' },
        ]}
      />

      {topTab === 'miniapp' ? (
        <MiniAppTexts botId={botId} />
      ) : (
        <>
          {bilingual ? (
            <div className="space-y-1.5">
              <SegmentControl<'ru' | 'en'>
                value={locale}
                onChange={switchLocale}
                options={[
                  { value: 'ru', label: 'Русский' },
                  { value: 'en', label: 'English' },
                ]}
              />
              <p className="px-1 text-[12px] text-dim leading-relaxed">
                {locale === 'en'
                  ? 'Тексты для клиентов с английским. Без вашей правки они видят наш английский текст.'
                  : 'Тексты для клиентов с русским.'}
              </p>
            </div>
          ) : null}
          {/* Второй переключатель: Тексты / Кнопки. */}
          <SegmentControl<BotTab>
            value={botTab}
            onChange={setBotTab}
            options={[
              { value: 'menu', label: 'Меню' },
              { value: 'texts', label: 'Тексты' },
              { value: 'buttons', label: 'Кнопки' },
              { value: 'custom', label: 'Свои' },
            ]}
          />

          {botTab === 'menu' && <MenuBuilder botId={botId} />}

          {botTab === 'custom' && <CustomButtonsTab botId={botId} />}

          {botTab !== 'custom' && botTab !== 'menu' && (
          <>
          {/* Инфо-плашка. */}
          <div className="flex gap-2.5 text-[12px] text-muted-foreground surface-inset rounded-[14px] p-3.5 leading-relaxed">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary-light" strokeWidth={1.9} />
            <span>
              Можно переписать тексты и кнопки под свой бренд. Системные надписи (цены, оплата)
              защищены. Переменные <code className="font-mono text-[11px] px-1 rounded bg-white/[0.07]">{'{…}'}</code>{' '}
              подставляются автоматически, не удаляйте их.
            </span>
          </div>

          {/* Иконки заданы, а мастер-тумблер выключен: в боте кнопки уходят без иконок.
              Премиум, поставленный через «Значок» (current_emoji), учитываем наравне с
              иконкой кнопки: он тоже молчит при выключенном тумблере, а раньше
              предупреждения на него не было вовсе. */}
          {data &&
            botTab === 'buttons' &&
            !data.premium_emoji_enabled &&
            data.entries.some(
              (e) => e.kind === 'button' && (e.current_icon || e.current_emoji?.id),
            ) && <PremiumEmojiWarning botId={botId} />}

          {data && botTab === 'texts' && (
            <NameFallbackCard botId={botId} current={data.name_fallback ?? 'друг'} />
          )}

          {data && <BulkTextsBar botId={botId} />}

          {isError && (
            <div className="surface-inset rounded-[14px] p-5 text-center text-sm text-muted-foreground">
              Не удалось загрузить. Попробуйте позже.
            </div>
          )}

          {isPending && (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14 rounded-[24px]" />
              ))}
            </div>
          )}

          {data && grouped.length === 0 && (
            <div className="surface-inset rounded-[14px] p-5 text-center text-sm text-muted-foreground">
              {botTab === 'texts' ? 'Настраиваемых текстов пока нет.' : 'Настраиваемых кнопок пока нет.'}
            </div>
          )}

          {data &&
            grouped.map(({ group, entries }) => (
              <Accordion
                key={`${botTab}-${group}`}
                title={GROUP_LABELS[group] ?? group}
                icon={
                  botTab === 'texts' ? (
                    <MessageSquare className="w-4 h-4" />
                  ) : (
                    <MousePointerClick className="w-4 h-4" />
                  )
                }
              >
                <div className="space-y-3">
                  {entries.map((entry) =>
                    // В ключе и текущий текст: после массовой правки строка с набранным
                    // локально текстом пересоберётся и не затрёт новое значение старым.
                    botTab === 'texts' ? (
                      <TextEntryRow key={`${entry.key}:${entry.current_override ?? ''}`} botId={botId} entry={entry} />
                    ) : (
                      <ButtonEntryRow key={`${entry.key}:${entry.current_override ?? ''}`} botId={botId} entry={entry} />
                    ),
                  )}
                </div>
              </Accordion>
            ))}
          </>
          )}
        </>
      )}
    </div>
  );
}

const CUSTOM_SECTIONS: Array<{ key: string; label: string; hint: string }> = [
  { key: 'menu', label: 'Главное меню', hint: 'Первый экран после /start' },
  { key: 'profile', label: 'Профиль', hint: 'Баланс, рефералы, промокод' },
  { key: 'subs', label: 'Мои подписки', hint: 'Список подписок клиента' },
];

/**
 * Свои кнопки партнёра. Три раздела, до трёх кнопок в каждом.
 *
 * Порядок меняется стрелками, а не перетаскиванием: внутри Telegram drag конфликтует
 * со свайпом закрытия мини-аппа, а элементов тут максимум три — стрелки быстрее.
 */
function CustomButtonsTab({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['custom-buttons', botId],
    queryFn: () => api.getCustomButtons(botId),
  });
  const [draft, setDraft] = useState<Record<string, CustomButton[]> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const sections = draft ?? data?.sections ?? {};
  const maxPer = data?.max_per_section ?? 3;
  const actions = data?.available_actions ?? [];
  const dirty = draft !== null;

  const save = useMutation({
    mutationFn: () => api.updateCustomButtons(botId, sections),
    onSuccess: (fresh) => {
      qc.setQueryData(['custom-buttons', botId], fresh);
      // Кнопки стоят и в раскладке меню: без перечитывания конструктор показал бы
      // прежний набор, без новой и с удалённой.
      qc.invalidateQueries({ queryKey: ['menu-layout', botId] });
      setDraft(null);
      setErr(null);
      haptic('success');
      toast.success('Кнопки сохранены');
    },
    onError: (e: Error) => {
      haptic('error');
      const body = (e as { body?: { error?: { message?: string } } }).body;
      setErr(body?.error?.message ?? e.message ?? 'Не сохранилось');
    },
  });

  const patch = (section: string, items: CustomButton[]) => {
    setErr(null);
    setDraft({ ...sections, [section]: items });
  };

  if (isPending) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 rounded-[24px]" />
        ))}
      </div>
    );
  }
  // Сохранение заменяет свои кнопки целиком. Без загруженного списка форма начиналась
  // бы с пустого, и первое же сохранение стёрло бы все остальные кнопки.
  if (!data) {
    return (
      <div className="surface-inset rounded-[14px] p-5 text-center text-sm text-muted-foreground">
        Не удалось загрузить. Попробуйте позже.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2.5 text-[12px] text-muted-foreground surface-inset rounded-[14px] p-3.5 leading-relaxed">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary-light" strokeWidth={1.9} />
        <span>
          Свои кнопки встают под нашими, до {maxPer} штук в разделе. Кнопка ведёт по
          ссылке, на экран мини-аппа или в раздел бота. В мини-приложении кнопки
          «Главного меню» и «Профиля» показываются в профиле, кнопки «Моих подписок»
          на экране подписки.
        </span>
      </div>

      {CUSTOM_SECTIONS.map((sec) => (
        <Accordion
          key={sec.key}
          title={`${sec.label} · ${(sections[sec.key] ?? []).length}/${maxPer}`}
          icon={<MousePointerClick className="w-4 h-4" />}
        >
          <p className="text-[11px] text-dim mb-2.5">{sec.hint}</p>
          <div className="space-y-2.5">
            {(sections[sec.key] ?? []).map((btn, i, arr) => (
              <CustomButtonRow
                key={i}
                button={btn}
                actions={actions}
                canUp={i > 0}
                canDown={i < arr.length - 1}
                onChange={(next) =>
                  patch(sec.key, arr.map((b, j) => (j === i ? next : b)))
                }
                onMove={(dir) => {
                  const next = [...arr];
                  const to = i + dir;
                  [next[i], next[to]] = [next[to], next[i]];
                  patch(sec.key, next);
                }}
                onRemove={() => patch(sec.key, arr.filter((_, j) => j !== i))}
              />
            ))}
            {(sections[sec.key] ?? []).length < maxPer && (
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  patch(sec.key, [...(sections[sec.key] ?? []), { text: '', url: '' }]);
                }}
                className="w-full surface-inset rounded-[13px] py-2.5 text-[13px] font-semibold text-muted-foreground active:scale-[0.98] transition-transform"
              >
                + Добавить кнопку
              </button>
            )}
          </div>
        </Accordion>
      ))}

      {err && <p className="text-[12px] text-danger leading-snug px-1">{err}</p>}

      <button
        type="button"
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
        className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
      >
        {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Сохранить кнопки
      </button>
    </div>
  );
}

/** Одна своя кнопка: подпись, тип, цель и перестановка. */
function CustomButtonRow({
  button,
  actions,
  canUp,
  canDown,
  onChange,
  onMove,
  onRemove,
}: {
  button: CustomButton;
  actions: Array<{ key: string; label: string }>;
  canUp: boolean;
  canDown: boolean;
  onChange: (next: CustomButton) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const isAction = button.kind === 'action' || (!button.url && !!button.action);

  return (
    <div className="surface-inset rounded-[14px] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={button.text}
          maxLength={64}
          placeholder="Подпись кнопки"
          onChange={(e) => onChange({ ...button, text: e.target.value })}
          className="flex-1 min-w-0 surface-card rounded-[11px] px-3 py-2 text-[13.5px] outline-none focus:border-white/30"
        />
        <button
          type="button"
          disabled={!canUp}
          onClick={() => onMove(-1)}
          aria-label="Выше"
          className="w-8 h-8 rounded-[10px] surface-card flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          disabled={!canDown}
          onClick={() => onMove(1)}
          aria-label="Ниже"
          className="w-8 h-8 rounded-[10px] surface-card flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Удалить кнопку"
          className="w-8 h-8 rounded-[10px] surface-card flex items-center justify-center text-danger active:scale-95 transition-transform"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <ButtonIconFields
        glyph={button.icon ?? ''}
        iconId={button.icon_id ?? ''}
        onChange={(glyph, iconId) => {
          const { icon: _i, icon_id: _d, ...rest } = button;
          onChange({ ...rest, ...buttonIconDraft(glyph, iconId) });
        }}
      />
      <ButtonStylePicker
        value={(button.style as ButtonStyle | undefined) ?? 'none'}
        onChange={(v) => {
          const { style: _s, ...rest } = button;
          onChange(v && v !== 'none' ? { ...rest, style: v } : rest);
        }}
      />

      <div className="flex items-center gap-2">
        <select
          value={isAction ? 'action' : 'url'}
          onChange={(e) => {
            // id сохраняем: по нему кнопка стоит в раскладке меню. Без него сервер
            // выдал бы новый, и кнопка уехала бы с места, куда её поставили, в конец.
            // Значок тоже переносим: смена вида кнопки его не касается.
            const base = {
              ...(button.id ? { id: button.id } : {}),
              ...(button.icon ? { icon: button.icon } : {}),
              ...(button.icon_id ? { icon_id: button.icon_id } : {}),
              ...(button.style ? { style: button.style } : {}),
            };
            onChange(
              e.target.value === 'action'
                ? { ...base, text: button.text, kind: 'action', action: actions[0]?.key ?? '' }
                : { ...base, text: button.text, url: '' },
            );
          }}
          className="surface-card rounded-[11px] px-2.5 py-2 text-[12.5px] outline-none shrink-0"
        >
          <option value="url">Ссылка</option>
          <option value="action">Наш раздел</option>
        </select>
        {isAction ? (
          <select
            value={button.action ?? ''}
            onChange={(e) => onChange({ ...button, action: e.target.value })}
            className="flex-1 min-w-0 surface-card rounded-[11px] px-2.5 py-2 text-[12.5px] outline-none"
          >
            {actions.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="url"
            inputMode="url"
            value={button.url ?? ''}
            placeholder="https://t.me/…"
            onChange={(e) => onChange({ ...button, url: e.target.value })}
            className="flex-1 min-w-0 surface-card rounded-[11px] px-3 py-2 text-[13px] outline-none focus:border-white/30"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Премиум-иконки кнопок заданы, но мастер-тумблер бота выключен: Telegram получает
 * кнопки без `icon_custom_emoji_id`, и партнёр видит «установлена» в кабинете при
 * пустой кнопке в боте. Даём включить прямо отсюда, не уводя в настройки бота.
 */
function PremiumEmojiWarning({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: () => api.updatePremiumEmoji(botId, true),
    onSuccess: () => {
      qc.setQueryData(
        ['bot-texts', botId],
        (prev: TextCatalog | undefined) => prev && { ...prev, premium_emoji_enabled: true },
      );
      qc.invalidateQueries({ queryKey: ['bot', botId] });
      haptic('success');
      toast.success('Премиум-эмодзи включены');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  return (
    <div className="flex gap-2.5 text-[12px] text-[#d6b88a] bg-[rgba(245,166,35,0.07)] border border-[rgba(245,166,35,0.22)] rounded-[14px] p-3.5 leading-relaxed">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.9} />
      <div className="space-y-2.5 flex-1 min-w-0">
        <div>
          <b className="text-[#f0c489]">Премиум-эмодзи у бота выключены.</b> Иконки сохранены,
          но в кнопках не показываются: для них нужен общий тумблер бота. Включите его, и меню
          обновится у клиентов при следующем открытии.
        </div>
        <button
          type="button"
          disabled={isPending}
          onClick={() => mutate()}
          className="px-3 py-1.5 rounded-[10px] bg-[rgba(245,166,35,0.16)] text-[#f0c489] text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform"
        >
          {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Включить премиум-эмодзи
        </button>
      </div>
    </div>
  );
}

// ============ МИНИ-АПП (client-webapp) ============

/** Вкладка мини-аппа: настраиваемые тексты клиентского приложения (только тексты). */
function MiniAppTexts({ botId }: { botId: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['bot-webapp-texts', botId],
    queryFn: () => api.getWebappTexts(botId),
    retry: 1,
  });

  const grouped = useMemo(() => {
    if (!data) return [];
    const byGroup = new Map<string, WebappTextEntry[]>();
    for (const e of data.entries) {
      const arr = byGroup.get(e.group) ?? [];
      arr.push(e);
      byGroup.set(e.group, arr);
    }
    const ordered = [
      ...data.groups.filter((g) => byGroup.has(g)),
      ...[...byGroup.keys()].filter((g) => !data.groups.includes(g)),
    ];
    return ordered.map((g) => ({ group: g, entries: byGroup.get(g)! }));
  }, [data]);

  return (
    <>
      <div className="flex gap-2.5 text-[12px] text-muted-foreground surface-inset rounded-[14px] p-3.5 leading-relaxed">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary-light" strokeWidth={1.9} />
        <span>
          Тексты клиентского мини-аппа (кнопки, заголовки, экраны). Обычный текст и эмодзи —
          без разметки. Цены, суммы и системные надписи защищены. Один текст показывается всем
          клиентам независимо от их языка.
        </span>
      </div>

      {isError && (
        <div className="surface-inset rounded-[14px] p-5 text-center text-sm text-muted-foreground">
          Не удалось загрузить. Попробуйте позже.
        </div>
      )}

      {isPending && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 rounded-[24px]" />
          ))}
        </div>
      )}

      {data && grouped.length === 0 && !isPending && (
        <div className="surface-inset rounded-[14px] p-5 text-center text-sm text-muted-foreground">
          Настраиваемых текстов пока нет.
        </div>
      )}

      {data &&
        grouped.map(({ group, entries }) => (
          <Accordion key={`ma-${group}`} title={group} icon={<MessageSquare className="w-4 h-4" />}>
            <div className="space-y-3">
              {entries.map((entry) => (
                <MiniAppTextCard key={entry.key} botId={botId} entry={entry} />
              ))}
            </div>
          </Accordion>
        ))}
    </>
  );
}

/** Карточка одного текста мини-аппа: plain-textarea + предпросмотр + сохранить/сбросить. */
function MiniAppTextCard({ botId, entry }: { botId: string; entry: WebappTextEntry }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string>(entry.current_override ?? entry.default_ru);
  const [err, setErr] = useState<string | null>(null);
  const onCatalog = (cat: WebappTextCatalog) =>
    qc.setQueryData(['bot-webapp-texts', botId], cat);

  const hasOverride = entry.current_override !== null;
  const dirty = draft !== (entry.current_override ?? entry.default_ru);

  const extractMsg = (e: unknown): string => {
    if (e instanceof ApiError && e.status === 422) {
      const body = e.body as { message?: string; detail?: string; error?: { message?: string } } | null;
      const msg = body?.error?.message ?? body?.message ?? body?.detail ?? e.message;
      return typeof msg === 'string' ? msg : 'Не прошло проверку';
    }
    return e instanceof Error ? e.message : 'Не сохранилось';
  };

  const save = useMutation({
    mutationFn: (text: string) => api.updateWebappTexts(botId, { [entry.key]: text }),
    onSuccess: (cat) => {
      onCatalog(cat);
      setErr(null);
      haptic('success');
      toast.success('Сохранено');
    },
    onError: (e) => {
      haptic('error');
      setErr(extractMsg(e));
    },
  });

  const reset = useMutation({
    mutationFn: () => api.updateWebappTexts(botId, { [entry.key]: null }),
    onSuccess: (cat) => {
      onCatalog(cat);
      setErr(null);
      setDraft(entry.default_ru);
      haptic('success');
      toast.success('Сброшено к стандартному');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось сбросить');
    },
  });

  const busy = save.isPending || reset.isPending;
  const over = draft.length > entry.max_len;

  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-3">
      <div>
        <div className="eyebrow mb-1">Стандартный текст</div>
        <p className="text-[12px] text-dim whitespace-pre-wrap break-words leading-snug">
          {entry.default_ru || '—'}
        </p>
      </div>

      {/* Предпросмотр в мини-аппе. */}
      <div className="space-y-1.5">
        <div className="text-[10.5px] uppercase tracking-wide text-dim font-medium">
          Предпросмотр в мини-аппе
        </div>
        <div className="surface-card rounded-[12px] px-3 py-2 text-[13.5px] font-medium break-words">
          {draft || entry.default_ru || '—'}
        </div>
      </div>

      <textarea
        value={draft}
        rows={draft.length > 60 ? 3 : 1}
        onChange={(e) => {
          setDraft(e.target.value);
          if (err) setErr(null);
        }}
        placeholder={entry.default_ru}
        className="w-full surface-card rounded-[12px] px-3.5 py-2.5 text-[13.5px] outline-none focus:border-white/30 resize-y leading-snug"
      />
      <div className="flex justify-end">
        <span className={`text-[11px] tabular-nums ${over ? 'text-danger' : 'text-dim'}`}>
          {draft.length}/{entry.max_len}
        </span>
      </div>

      {err && <p className="text-[12px] text-danger leading-snug">{err}</p>}

      <SaveResetRow
        onSave={() => save.mutate(draft)}
        onReset={() => reset.mutate()}
        saving={save.isPending}
        resetting={reset.isPending}
        canSave={!busy && dirty && !over && draft.trim().length > 0}
        canReset={!busy && hasOverride}
      />
    </div>
  );
}

// ============ ТЕКСТЫ ============

/** Диспетчер для текстовой записи: редактор или системная строка со сменой значка. */
function TextEntryRow({ botId, entry }: { botId: string; entry: TextEntryDTO }) {
  if (!entry.editable) {
    return <SystemEntryRow botId={botId} entry={entry} />;
  }
  return <EditableTextCard botId={botId} entry={entry} />;
}

/**
 * Системный ключ: текст остаётся нашим, но значок партнёр меняет под свой бренд.
 * Раньше здесь был только замок, и партнёры просили «хотя бы смайлик поменять».
 */
function SystemEntryRow({ botId, entry }: { botId: string; entry: TextEntryDTO }) {
  const qc = useQueryClient();
  const iconColor = iconColorByKey(qc.getQueryData<TextCatalog>(['bot-texts', botId])?.icon_color);
  const [open, setOpen] = useState(false);
  const [premOpen, setPremOpen] = useState(false);
  const [draft, setDraft] = useState(entry.current_emoji?.glyph ?? '');
  const [err, setErr] = useState<string | null>(null);

  const premiumId = entry.current_emoji?.id || null;

  // Премиум-эмодзи хранится только вместе с обычным значком: он же фолбэк для
  // клиентов без Premium, а бэк без glyph снимает переопределение целиком.
  const save = useMutation({
    mutationFn: (v: { glyph: string | null; emojiId: string | null }) =>
      api.updateEmojiOverride(botId, entry.key, v.glyph, v.emojiId),
    onSuccess: (cat) => {
      qc.setQueryData(['bot-texts', botId], cat);
      setErr(null);
      setOpen(false);
      setPremOpen(false);
      haptic('success');
    },
    onError: (e: Error) => {
      haptic('error');
      setErr(e.message || 'Не удалось сохранить значок');
    },
  });

  const current = entry.current_emoji?.glyph ?? entry.default_emoji ?? '';
  const dirty = draft.trim() !== (entry.current_emoji?.glyph ?? '');

  return (
    <div className="surface-inset rounded-[14px] p-3.5">
      <div className="flex items-center gap-2.5">
        <Lock className="w-4 h-4 text-dim shrink-0" strokeWidth={1.8} />
        <span className="flex-1 min-w-0 text-[13px] truncate opacity-70" title={entry.system_reason ?? undefined}>
          {entry.default_ru || entry.key}
        </span>
        {entry.emoji_editable !== false && (
          <button
            type="button"
            onClick={() => {
              haptic('selection');
              setOpen((v) => !v);
            }}
            className="shrink-0 rounded-[10px] px-2.5 py-1 text-[15px] leading-none surface-card"
            title="Сменить значок"
          >
            {current || '＋'}
          </button>
        )}
      </div>
      {entry.system_reason && (
        <p className="text-[11px] text-dim mt-1.5 leading-snug opacity-70">{entry.system_reason}</p>
      )}

      {open && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
          <p className="text-[11px] text-dim leading-snug">
            Текст здесь наш, а значок ваш. Оставьте поле пустым, чтобы вернуть стандартный.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={draft}
              maxLength={8}
              onChange={(e) => {
                setDraft(e.target.value);
                if (err) setErr(null);
              }}
              placeholder={entry.default_emoji ?? '🙂'}
              className="w-20 surface-card rounded-[12px] px-3 py-2 text-[15px] text-center outline-none focus:border-white/30"
            />
            <button
              type="button"
              disabled={!dirty || save.isPending}
              onClick={() => {
                const glyph = draft.trim();
                // Правка значка не должна ронять уже поставленную премиум-иконку.
                save.mutate({ glyph: glyph || null, emojiId: glyph ? premiumId : null });
              }}
              className="rounded-[12px] px-3.5 py-2 text-[12.5px] font-semibold surface-card disabled:opacity-40"
            >
              {save.isPending ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </div>

          {/* Премиум-эмодзи. Работает и здесь: текст остаётся нашим, анимированным
              становится только значок. */}
          <div className="pt-2 border-t border-white/5 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary-light shrink-0" strokeWidth={2} />
              <span className="text-[12px] font-semibold flex-1">Премиум-значок</span>
            </div>
            <p className="text-[11px] text-dim leading-snug">
              Нужен Telegram Premium у владельца бота. Обычный значок остаётся запасным —
              его увидят те, у кого Premium нет.
            </p>
            <div className="flex items-center gap-2">
              {premiumId ? (
                <>
                  <span className="flex items-center gap-1.5 surface-card rounded-[10px] px-2.5 py-1.5 text-[13px]">
                    <PremojiPreview id={premiumId} alt={entry.current_emoji?.glyph ?? ''} />
                    <span className="text-dim text-[11px]">установлен</span>
                  </span>
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({ glyph: entry.current_emoji?.glyph ?? null, emojiId: null })
                    }
                    className="px-3 py-1.5 rounded-[10px] surface-card text-[12px] font-semibold text-muted-foreground flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform"
                  >
                    <X className="w-3.5 h-3.5" />
                    Убрать
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setErr(null);
                    setPremOpen(true);
                  }}
                  className="px-3 py-1.5 rounded-[10px] bg-white/[0.07] text-primary-light text-[12px] font-semibold flex items-center gap-1.5 active:scale-[0.98] transition-transform"
                >
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                  Поставить премиум-значок
                </button>
              )}
            </div>
          </div>

          {err && <p className="text-[12px] text-danger leading-snug">{err}</p>}
        </div>
      )}

      <BottomSheet open={premOpen} onClose={() => setPremOpen(false)} title="Премиум-значок">
        <PremiumEmojiGuide
          submitLabel="Поставить значок"
          catalog={{ color: iconColor }}
          onSubmit={({ id, alt }) => {
            // alt — запасной значок для клиентов без Premium; он же станет
            // обычным значком, если партнёр своего ещё не задал.
            const glyph = draft.trim() || entry.current_emoji?.glyph || alt;
            setDraft(glyph);
            save.mutate({ glyph, emojiId: id });
          }}
        />
      </BottomSheet>
    </div>
  );
}

/** Слово вместо `{first_name}`, когда у человека в Telegram нет имени (заявка #206). */
function NameFallbackCard({ botId, current }: { botId: string; current: string }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(current);
  useEffect(() => setValue(current), [current]);
  const save = useMutation({
    mutationFn: (v: string) => api.setNameFallback(botId, v),
    onSuccess: (cat) => {
      qc.setQueryData(['bot-texts', botId], cat);
      setValue(cat.name_fallback ?? 'друг');
      haptic('success');
      toast.success('Сохранено');
    },
    onError: (e) => {
      haptic('error');
      toast.error(e instanceof Error ? e.message : 'Не сохранилось');
    },
  });
  const clean = value.trim().replace(/\s+/g, ' ');
  const dirty = clean !== current && !(clean === '' && current === 'друг');
  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-2">
      <div className="eyebrow">Если в Telegram нет имени</div>
      <p className="text-[12px] text-dim leading-snug">
        В текстах можно писать <code className="font-mono text-[11px] px-1 rounded bg-white/[0.07]">{'{first_name}'}</code>:
        «Привет, Иван!». Без имени бот напишет «Привет, {clean || 'друг'}!»
      </p>
      <div className="flex gap-2">
        <input
          value={value}
          maxLength={32}
          placeholder="друг"
          onChange={(e) => setValue(e.target.value)}
          className="min-w-0 flex-1 h-11 rounded-[12px] bg-white/[0.04] border border-white/10 px-3 text-[15px] outline-none focus:border-white/30"
        />
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(clean)}
          className="h-11 shrink-0 rounded-[12px] bg-primary px-4 text-[14px] font-semibold text-white disabled:opacity-40 active:scale-[0.97] transition-transform"
        >
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}

/** Хук сохранения/сброса одного override — общий для текста и подписи кнопки. */
function useEntrySave(botId: string, entry: TextEntryDTO) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const onCatalog = (cat: TextCatalog) => qc.setQueryData(['bot-texts', botId], cat);

  const extractMsg = (e: unknown): string => {
    if (e instanceof ApiError && e.status === 422) {
      const body = e.body as { message?: string; detail?: string; error?: { message?: string } } | null;
      const msg = body?.error?.message ?? body?.message ?? body?.detail ?? e.message;
      return typeof msg === 'string' ? msg : 'Не прошло проверку';
    }
    return e instanceof Error ? e.message : 'Не сохранилось';
  };

  const save = useMutation({
    mutationFn: (text: string) => api.updateBotTexts(botId, { [entry.key]: text }),
    onSuccess: (cat) => {
      onCatalog(cat);
      setErr(null);
      haptic('success');
      toast.success('Сохранено');
    },
    onError: (e) => {
      haptic('error');
      setErr(extractMsg(e));
    },
  });

  const reset = useMutation({
    mutationFn: () => api.updateBotTexts(botId, { [entry.key]: null }),
    onSuccess: (cat) => {
      onCatalog(cat);
      setErr(null);
      haptic('success');
      toast.success('Сброшено к стандартному');
    },
    onError: () => {
      haptic('error');
      toast.error('Не удалось сбросить');
    },
  });

  return { err, setErr, save, reset, onCatalog };
}

function EditableTextCard({ botId, entry }: { botId: string; entry: TextEntryDTO }) {
  // Каталог уже в кэше у страницы: отсюда переменные имени (`{first_name}`, #206).
  const catalogQ = useQuery({
    queryKey: ['bot-texts', botId],
    queryFn: () => api.getBotTexts(botId, 1),
    staleTime: Infinity,
  });
  const [draft, setDraft] = useState<string>(entry.current_override ?? entry.default_ru);
  const [editorKey, setEditorKey] = useState(0);
  const { err, setErr, save, reset } = useEntrySave(botId, entry);

  const hasOverride = entry.current_override !== null;
  const dirty = draft !== (entry.current_override ?? entry.default_ru);
  const busy = save.isPending || reset.isPending;

  const doReset = () => {
    reset.mutate(undefined, {
      onSuccess: () => {
        setDraft(entry.default_ru);
        setEditorKey((k) => k + 1);
      },
    });
  };

  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-3">
      <div>
        <div className="eyebrow mb-1">Стандартный текст</div>
        <p className="text-[12px] text-dim whitespace-pre-wrap break-words leading-snug">
          {entry.default_ru || '—'}
        </p>
      </div>

      <TelegramEditor
        key={editorKey}
        value={entry.current_override ?? entry.default_ru}
        placeholders={entry.placeholders}
        userVars={catalogQ.data?.user_vars}
        nameFallback={catalogQ.data?.name_fallback}
        onChange={(html) => {
          setDraft(html);
          if (err) setErr(null);
        }}
        maxLen={entry.max_len}
      />

      <BotBubblePreview
        html={draft}
        premium={Boolean(catalogQ.data?.premium_emoji_enabled)}
        samples={{
          ...(catalogQ.data?.samples ?? {}),
          ...Object.fromEntries((catalogQ.data?.user_vars ?? []).map((v) => [v.name, v.example])),
          ...(catalogQ.data?.key_samples?.[entry.key] ?? {}),
        }}
      />

      {err && <p className="text-[12px] text-danger leading-snug">{err}</p>}

      <SaveResetRow
        onSave={() => save.mutate(draft)}
        onReset={doReset}
        saving={save.isPending}
        resetting={reset.isPending}
        canSave={!busy && dirty}
        canReset={!busy && hasOverride}
      />
    </div>
  );
}

// ============ КНОПКИ ============

/** Диспетчер для кнопочной записи. Системной подписи меняем только значок. */
function ButtonEntryRow({ botId, entry }: { botId: string; entry: TextEntryDTO }) {
  if (!entry.editable) {
    return <SystemEntryRow botId={botId} entry={entry} />;
  }
  return <EditableButtonCard botId={botId} entry={entry} />;
}

function EditableButtonCard({ botId, entry }: { botId: string; entry: TextEntryDTO }) {
  const qc = useQueryClient();
  const iconColor = iconColorByKey(qc.getQueryData<TextCatalog>(['bot-texts', botId])?.icon_color);
  const [draft, setDraft] = useState<string>(entry.current_override ?? entry.default_ru);
  const { err, setErr, save, reset } = useEntrySave(botId, entry);
  const [iconOpen, setIconOpen] = useState(false);
  const [iconErr, setIconErr] = useState<string | null>(null);

  const hasOverride = entry.current_override !== null;
  const dirty = draft !== (entry.current_override ?? entry.default_ru);
  const busy = save.isPending || reset.isPending;

  const onCatalog = (cat: TextCatalog) => qc.setQueryData(['bot-texts', botId], cat);

  const setIcon = useMutation({
    mutationFn: (v: { id: string; glyph: string } | null | 'off') =>
      v === 'off'
        ? api.updateButtonIcon(botId, entry.key, null, null, true)
        : v
          ? api.updateButtonIcon(botId, entry.key, v.id, v.glyph)
          : api.updateButtonIcon(botId, entry.key, null),
    onSuccess: (cat) => {
      onCatalog(cat);
      setIconErr(null);
      setIconOpen(false);
      haptic('success');
      toast.success('Иконка обновлена');
    },
    onError: (e) => {
      haptic('error');
      if (e instanceof ApiError && e.status === 422) {
        const body = e.body as { message?: string; error?: { message?: string } } | null;
        setIconErr(body?.error?.message ?? body?.message ?? e.message);
      } else {
        setIconErr(e instanceof Error ? e.message : 'Не удалось');
      }
    },
  });

  const doReset = () => {
    reset.mutate(undefined, {
      onSuccess: () => setDraft(entry.default_ru),
    });
  };

  const icon = entry.current_icon;

  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-3">
      <div className="eyebrow">Подпись кнопки</div>
      {KEY_HINTS[entry.key] ? (
        <div className="-mt-2 text-[11.5px] text-dim">{KEY_HINTS[entry.key]}</div>
      ) : null}

      {/* Предпросмотр кнопки: премиум-иконка (если есть) + подпись. */}
      <div className="space-y-1.5">
        <div className="text-[10.5px] uppercase tracking-wide text-dim font-medium">
          Предпросмотр в боте
        </div>
        <div className="flex items-center gap-1.5 surface-card rounded-[12px] px-3 py-2 text-[13.5px] font-medium">
          {icon && <PremojiPreview id={icon.id} alt={icon.glyph} />}
          <span className="truncate">{draft || entry.default_ru || '—'}</span>
        </div>
      </div>

      <div className="text-[10.5px] uppercase tracking-wide text-dim font-medium">Текст кнопки</div>
      <input
        type="text"
        value={draft}
        maxLength={entry.max_len}
        onChange={(e) => {
          setDraft(e.target.value);
          if (err) setErr(null);
        }}
        placeholder={entry.default_ru}
        className="w-full surface-card rounded-[12px] px-3.5 py-2.5 text-[13.5px] outline-none focus:border-white/30"
      />
      <div className="flex justify-end">
        <span className="text-[11px] tabular-nums text-dim">{draft.length}/{entry.max_len}</span>
      </div>

      {err && <p className="text-[12px] text-danger leading-snug">{err}</p>}

      <SaveResetRow
        onSave={() => save.mutate(draft)}
        onReset={doReset}
        saving={save.isPending}
        resetting={reset.isPending}
        canSave={!busy && dirty}
        canReset={!busy && hasOverride}
      />

      {/* Премиум-иконка кнопки. */}
      <div className="pt-1 border-t border-white/5 space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary-light shrink-0" strokeWidth={2} />
          <span className="text-[12px] font-semibold flex-1">Премиум-иконка кнопки</span>
        </div>
        <p className="text-[11px] text-dim leading-snug">
          Премиум-эмодзи <b className="text-muted-foreground">перед</b> текстом кнопки. Нужен Telegram
          Premium у владельца бота.
        </p>
        {iconErr && <p className="text-[12px] text-danger leading-snug">{iconErr}</p>}
        <div className="flex items-center gap-2">
          {icon ? (
            <>
              <span className="flex items-center gap-1.5 surface-card rounded-[10px] px-2.5 py-1.5 text-[13px]">
                <PremojiPreview id={icon.id} alt={icon.glyph} />
                <span className="text-dim text-[11px]">установлена</span>
              </span>
              <button
                type="button"
                disabled={setIcon.isPending}
                onClick={() => setIcon.mutate(null)}
                className="px-3 py-1.5 rounded-[10px] surface-card text-[12px] font-semibold text-muted-foreground flex items-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform"
              >
                {setIcon.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                Убрать
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIconErr(null);
                setIconOpen(true);
              }}
              className="px-3 py-1.5 rounded-[10px] bg-white/[0.07] text-primary-light text-[12px] font-semibold flex items-center gap-1.5 active:scale-[0.98] transition-transform"
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              Поставить премиум-иконку
            </button>
          )}
        </div>

        {/* «Без значка» отдельно от «Убрать»: та лишь стирает переопределение и
            возвращает наш дефолт, поэтому календарь у списка тарифов было не убрать. */}
        <button
          type="button"
          disabled={setIcon.isPending}
          onClick={() => setIcon.mutate(entry.icon_disabled ? null : 'off')}
          className="text-[11px] font-semibold text-dim disabled:opacity-40"
        >
          {entry.icon_disabled ? 'Вернуть наш значок' : 'Показывать кнопку без значка'}
        </button>
      </div>

      <BottomSheet open={iconOpen} onClose={() => setIconOpen(false)} title="Премиум-иконка кнопки">
        <PremiumEmojiGuide
          submitLabel="Поставить иконку"
          catalog={{ color: iconColor }}
          onSubmit={({ id, alt }) => setIcon.mutate({ id, glyph: alt })}
        />
      </BottomSheet>
    </div>
  );
}

// ============ Общие ============

function SaveResetRow({
  onSave,
  onReset,
  saving,
  resetting,
  canSave,
  canReset,
}: {
  onSave: () => void;
  onReset: () => void;
  saving: boolean;
  resetting: boolean;
  canSave: boolean;
  canReset: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={!canSave}
        onClick={onSave}
        className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-[12px] text-[13.5px] font-bold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Сохранить
      </button>
      <button
        type="button"
        disabled={!canReset}
        onClick={onReset}
        title="Вернуть стандартное"
        className="px-3.5 py-2.5 rounded-[12px] surface-inset text-[13px] font-semibold text-muted-foreground flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-[0.98] transition-transform"
      >
        {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        Сбросить
      </button>
    </div>
  );
}
